import {
  Box,
  Button,
  Checkbox,
  CustomClasses,
  Dropdown,
  Input,
  Option,
  Popup,
  Table,
  TableRow,
  TopBar,
  YearlyCalendar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { Dayjs } from 'dayjs';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AbsenceReason,
  DayPart,
  Holiday,
  SchambeckUser,
  calculateVacationDaysForDateRange,
  createHoliday,
  generateNotification,
  getScheduledWorkTimeForRange,
  loadSchambeckUserById,
  updateHoliday,
  useData,
} from 'shared';
import { NotificationType } from 'shared/src/notification/notification.types';
import dayjs from 'shared/src/tools/Dayjs';
import { Right } from 'shared/src/userRole/UserRole.types';

import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { NotAllowed } from '../../components/NotAllowed';
import { useUser } from '../../components/UserContext';
import { generateDropdownOptionsForAbsenceReasons } from '../../utils/absenceReason/AbsenceReason.utils';
import { createComment } from '../../utils/comment/Comment.utils';
import {
  checkIfDayIsInRange,
  generateEmptyHoliday,
  holidayHasOverlap,
} from '../../utils/holiday/Holiday.utils';
import { generateDropdownOptionsForYear } from '../../utils/timeTracking/TimeTracking.utils';
import {
  getLastOvertimeForUser,
  getUserNameForSimpleUser,
  isResponsibleHREmployee,
  isUserAllowedToDo,
} from '../../utils/user/User.utils';

const HolidayOverview: React.FC = () => {
  const { axios, user } = useUser();
  const { t } = useTranslation();
  const [vacationDaysLeft, setVacationDaysLeft] = useState<number>(user.availableVacationDays);
  const [showConfirmationPopup, toggleShowConfirmationPopup] = useState<boolean>(false);
  const location = useLocation<{ userId: string }>();
  const userId: string = location.state?.userId ? location.state.userId : user.id;
  const [editUser, setEditUser] = useState<SchambeckUser>(user);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  const [customClasses, setCustomClasses] = useState<CustomClasses>({});
  const [newHolidayRequest, setNewHolidayRequest] = useState<Holiday>({
    ...generateEmptyHoliday({
      userId,
      officeId: user.contractInformation.currentOfficeId,
    }),
  });
  const [officialHolidays, setOfficialHolidays] = useState<Dayjs[]>([]);
  const [holidayToDisplay, setHolidayToDisplay] = useState<Holiday>();
  const { data: loadedHolidays, mutate: mutateHolidays } = useData('HOLIDAYS_ALL_USER', {
    params: {
      userId,
    },
    config: { fallbackData: [] },
  });

  const { data: loadedOfficialHolidays } = useData('OFFICIAL_HOLIDAYS_ALL', {
    config: {
      userId,
      fallbackData: [],
    },
  });

  const { data: loadedReasons } = useData('ABSENCE_REASONS_USER', {
    config: { fallbackData: [] },
    params: { userId: user.id },
  });

  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const { data: loadedSimpleUsers } = useData('SIMPLEUSERS_ALL_ALL_OFFICES', {
    config: { fallbackData: [] },
  });
  const isReadOnly: boolean = useMemo(() => {
    return editUser.contractInformation.workSchedules.length === 0 || !!newHolidayRequest.id;
  }, [editUser.contractInformation.workSchedules.length, newHolidayRequest.id]);

  /**
   * Disables the submit button when values are missing or readOnly is true
   */
  const disableSubmit: boolean = useMemo(
    (): boolean =>
      isReadOnly ||
      dayjs(newHolidayRequest.endDate).isBefore(newHolidayRequest.startDate) ||
      !newHolidayRequest.reasonId,
    [isReadOnly, newHolidayRequest],
  );
  //If another user edits the holidays of a user, we load the user to edit
  useEffect(() => {
    if (location.state?.userId)
      loadSchambeckUserById(location.state.userId, axios).then((loadedUser) => {
        if (loadedUser) {
          setEditUser(loadedUser);
          setNewHolidayRequest((prev) => ({
            ...prev,
            officeId:
              loadedUser.contractInformation.currentOfficeId ||
              loadedUser.contractInformation.officeId,
          }));
          setVacationDaysLeft(loadedUser.availableVacationDays);
        }
      });
    else {
      setEditUser(user);
      setNewHolidayRequest((prev) => ({
        ...prev,
        officeId: user.contractInformation.currentOfficeId || user.contractInformation.officeId,
      }));
      setVacationDaysLeft(user.availableVacationDays);
    }
  }, [axios, location.state?.userId, user]);

  //useEffect hook to setOfficialHolidays after they are loaded
  useEffect(() => {
    const dayJsOfficialHoliday: Dayjs[] = loadedOfficialHolidays.map((holiday) =>
      dayjs(holiday.date),
    );
    setOfficialHolidays(dayJsOfficialHoliday);
  }, [loadedOfficialHolidays]);

  /**
   * useEffect hook to set customClasses for the yearly calendar
   * object keys are used as css classnames for the specific dates
   */
  useEffect(() => {
    const azkReasons: string[] = loadedReasons
      .filter((reason) => reason.azkReduction)
      .map((reason) => reason.id);
    const config: CustomClasses = {
      holidayApproved: (day: Dayjs) =>
        loadedHolidays.some(
          (holiday) =>
            checkIfDayIsInRange(day, holiday.startDate, holiday.endDate) &&
            holiday.approved &&
            holiday.reasonId &&
            !azkReasons.includes(holiday.reasonId),
        ),
      holidayNotApproved: (day: Dayjs) =>
        loadedHolidays.some(
          (holiday) =>
            checkIfDayIsInRange(day, holiday.startDate, holiday.endDate) &&
            !holiday.approved &&
            !holiday.denied &&
            holiday.reasonId &&
            !azkReasons.includes(holiday.reasonId),
        ),
      absenceApproved: (day: Dayjs) =>
        loadedHolidays.some(
          (holiday) =>
            checkIfDayIsInRange(day, holiday.startDate, holiday.endDate) &&
            holiday.approved &&
            holiday.reasonId &&
            azkReasons.includes(holiday.reasonId),
        ),
      absenceNotApproved: (day: Dayjs) =>
        loadedHolidays.some(
          (holiday) =>
            checkIfDayIsInRange(day, holiday.startDate, holiday.endDate) &&
            !holiday.approved &&
            !holiday.denied &&
            holiday.reasonId &&
            azkReasons.includes(holiday.reasonId),
        ),
      officialHolidays:
        loadedOfficialHolidays.map((holiday) => holiday.date.toISOString().split('T')[0]) || [],
      weekend: 'Sat,Sun',
    };
    setCustomClasses(config);
  }, [loadedHolidays, loadedOfficialHolidays, editUser.availableVacationDays, loadedReasons]);

  /**
   * Helper function to handle the creation of a new holidayRequest
   */
  const handleHolidayRequest = async (confirmed?: boolean): Promise<void> => {
    if (!confirmed && holidayHasOverlap(newHolidayRequest, loadedHolidays)) {
      toggleShowConfirmationPopup(true);
      return Promise.resolve();
    }
    return createHoliday(newHolidayRequest, axios).then((createdHoliday) => {
      generateNotification({
        type: NotificationType.SUCCESS,
        value: t('general.notification.success.holidayRequest'),
      });
      mutateHolidays([...loadedHolidays, createdHoliday]);
      toggleShowConfirmationPopup(false);
    });
  };
  /**
   * Holds information about the currenty selected reasons type
   */
  const isAzkReduction: boolean = useMemo(
    () => !!loadedReasons.find((reason) => reason.id === newHolidayRequest.reasonId)?.azkReduction,
    [newHolidayRequest.reasonId],
  );
  /**
   * Helper method to update a holidayRequests endDate or startDate
   * @param date  new start or endDate
   * @param isStart boolean to decide if the method updates the start or endDate
   */
  const handleUpdateHolidayRequest = (date: Date, isStart: boolean) => {
    if (isStart) {
      const totalAmount: number = isAzkReduction
        ? getScheduledWorkTimeForRange(
            editUser.contractInformation.workSchedules,
            date,
            newHolidayRequest.endDate,
            loadedOfficialHolidays,
          )
        : calculateVacationDaysForDateRange(
            date,
            newHolidayRequest.endDate,
            editUser.contractInformation.workSchedules,
            officialHolidays,
            !!newHolidayRequest.onlyOn,
          ) || 0;
      setNewHolidayRequest({
        ...newHolidayRequest,
        startDate: date,
        totalAmount,
        onlyOn: dayjs(newHolidayRequest.endDate).isSame(date, 'day')
          ? newHolidayRequest.onlyOn
          : undefined,
      });
    } else {
      const totalAmount: number = isAzkReduction
        ? getScheduledWorkTimeForRange(
            editUser.contractInformation.workSchedules,
            newHolidayRequest.startDate,
            date,
            loadedOfficialHolidays,
          )
        : calculateVacationDaysForDateRange(
            newHolidayRequest.startDate,
            date,
            editUser.contractInformation.workSchedules,
            officialHolidays,
            !!newHolidayRequest.onlyOn,
          ) || 0;
      setNewHolidayRequest({
        ...newHolidayRequest,
        endDate: date,
        totalAmount,
        onlyOn: dayjs(newHolidayRequest.startDate).isSame(date, 'day')
          ? newHolidayRequest.onlyOn
          : undefined,
      });
    }
  };

  /**
   * Options to choose from when creating a holidayRequest
   */
  const reasonOptions: Option[] = useMemo((): Option[] => {
    const holidayReasons: AbsenceReason[] = loadedReasons.filter((reason) => reason.holidayReason);
    return generateDropdownOptionsForAbsenceReasons(holidayReasons, user.language);
  }, [loadedReasons, user.language]);

  /**
   * Helper method to update a holidayRequest
   */
  const handleUpdateHoliday = async (): Promise<void> => {
    if (!newHolidayRequest) return Promise.resolve();
    return updateHoliday(axios, newHolidayRequest).then((success) => {
      if (success)
        setNewHolidayRequest({
          ...generateEmptyHoliday({ userId: editUser.id }),
        });
    });
  };

  /**
   * Holds the existing requests as tableRows
   */
  const requestRows: TableRow[] = useMemo((): TableRow[] => {
    return loadedHolidays.map((holiday) => ({
      id: holiday.id,
      actions:
        isResponsibleHREmployee(user, editUser, loadedOffices) &&
        dayjs(holiday.endDate).isAfter(new Date())
          ? [
              <IconComponent
                type={IconType.EDIT}
                onClick={() =>
                  setNewHolidayRequest({
                    ...holiday,
                    changes: [
                      ...holiday.changes,
                      {
                        changedBy: user.id,
                        comment: '',
                        createDate: new Date(),
                        oldAmount: newHolidayRequest.totalAmount,
                        oldEnd: newHolidayRequest.endDate,
                        oldStart: newHolidayRequest.startDate,
                        oldReasonId: newHolidayRequest.reasonId || '',
                      },
                    ],
                  })
                }
                width={30}
              />,
            ]
          : [],
      content: [
        holiday.createDate.toLocaleDateString('DE-de'),
        holiday.startDate.toLocaleDateString('DE-de'),
        holiday.endDate.toLocaleDateString('DE-de'),
        t(
          `pages.holiday.overview.${
            holiday.approved ? 'approved' : holiday.denied ? 'denied' : 'pending'
          }`,
        ),
        holiday.totalAmount,
        holiday.decisionComment?.content || '',
        getUserNameForSimpleUser(holiday.decidedBy, loadedSimpleUsers),
      ],
    }));
  }, [loadedHolidays, user, editUser, loadedOffices, t, loadedSimpleUsers, newHolidayRequest]);

  /**
   * Displays the data for vacationdaysLeft or the saldo depending on the reason
   */
  const saldoInformation: ReactNode = useMemo(() => {
    const reason: AbsenceReason | undefined = loadedReasons.find(
      (reason) => reason.id === newHolidayRequest.reasonId,
    );
    const scheduledWorkTime: number = getScheduledWorkTimeForRange(
      editUser.contractInformation.workSchedules,
      newHolidayRequest.startDate,
      newHolidayRequest.endDate,
      loadedOfficialHolidays,
    );
    if (reason?.azkReduction) {
      const overTimeSaldoLastMonth: number = getLastOvertimeForUser(editUser)?.overTimeEnd || 0;
      return (
        <>
          <p>
            {t(`pages.holiday.overview.azkReductionAmount`, {
              replace: { amount: scheduledWorkTime.toFixed(2) },
            })}
          </p>
          <p>
            {t(`pages.holiday.overview.azkReductionSaldo`, {
              replace: {
                amount: (overTimeSaldoLastMonth - scheduledWorkTime).toFixed(2),
              },
            })}
          </p>
        </>
      );
    }
    return (
      <>
        <p>
          {t('pages.holiday.overview.usedHolidays')}
          {calculateVacationDaysForDateRange(
            newHolidayRequest.startDate,
            newHolidayRequest.endDate,
            editUser.contractInformation.workSchedules,
            officialHolidays,
            !!newHolidayRequest.onlyOn,
          )}
        </p>
        <p>
          {t('pages.holiday.overview.remainingHolidays')}
          {vacationDaysLeft - newHolidayRequest.totalAmount}
        </p>
      </>
    );
  }, [
    editUser.contractInformation.workSchedules,
    loadedOfficialHolidays,
    loadedReasons,
    newHolidayRequest.endDate,
    newHolidayRequest.reasonId,
    newHolidayRequest.startDate,
    newHolidayRequest.totalAmount,
    officialHolidays,
    t,
    user.contractInformation.yearlyTimes,
    vacationDaysLeft,
  ]);

  return isUserAllowedToDo(user.right, Right.HOLIDAY_OVERVIEW) ? (
    <div className="holiday-overview__wrapper">
      <Popup
        isOpen={!!newHolidayRequest.id}
        onClose={() => setNewHolidayRequest(generateEmptyHoliday({ userId: editUser.id }))}
        title={t('pages.holiday.overview.popupTitle')}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => setNewHolidayRequest(generateEmptyHoliday({ userId: editUser.id }))}
          />,
          <Button value={t('general.buttons.save')} onClick={() => handleUpdateHoliday()} />,
        ]}
      >
        <div className="three-columns">
          <Input
            label={t('pages.holiday.overview.newRequestStart')}
            type="date"
            value={newHolidayRequest.startDate}
            onChangeDate={(startDate) => {
              if (startDate) handleUpdateHolidayRequest(startDate, true);
            }}
          />
          <Input
            label={t('pages.holiday.overview.newRequestEnd')}
            type="date"
            value={newHolidayRequest.endDate}
            onChangeDate={(endDate) => {
              if (endDate) handleUpdateHolidayRequest(endDate, false);
            }}
          />
          <Dropdown
            selectedOption={newHolidayRequest.reasonId}
            options={reasonOptions}
            onChange={(reasonId) => setNewHolidayRequest({ ...newHolidayRequest, reasonId })}
            label={t('pages.holiday.overview.reason')}
          />
          <Input
            label={t('pages.holiday.overview.newRequestMessage')}
            type="text"
            value={newHolidayRequest.requestComment?.content || ''}
            onChange={(content) =>
              setNewHolidayRequest({
                ...newHolidayRequest,
                requestComment: { ...createComment(user.id, content) },
              })
            }
          />
        </div>
      </Popup>
      <Popup
        isOpen={showConfirmationPopup}
        onClose={() => toggleShowConfirmationPopup(false)}
        title={t('pages.holiday.overview.confirmationPopup.title')}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => toggleShowConfirmationPopup(false)}
          />,
          <Button
            value={t('general.buttons.confirm')}
            onClick={() => handleHolidayRequest(true)}
          />,
        ]}
      >
        <p>{t('pages.holiday.overview.confirmationPopup.content')}</p>
      </Popup>
      <TopBar
        title={
          location.state?.userId
            ? t('pages.holiday.overview.headlineEdit', {
                replace: {
                  EMPLOYEE: getUserNameForSimpleUser(location.state.userId, loadedSimpleUsers),
                },
              })
            : t('pages.holiday.overview.headline')
        }
      />
      {holidayToDisplay && (
        <div className="holiday-overview__detail-wrapper">
          <div className="holiday-overview__detail-wrapper__title-wrapper">
            <p className="holiday-overview__detail-wrapper__description">
              {t('pages.holiday.overview.range', {
                replace: {
                  start: holidayToDisplay.startDate.toLocaleDateString('DE-de'),
                  end: holidayToDisplay.endDate.toLocaleDateString('DE-de'),
                },
              })}
            </p>
            <IconComponent
              type={IconType.CLOSE}
              width={20}
              onClick={() => setHolidayToDisplay(undefined)}
            />
          </div>
          <p className="holiday-overview__detail-wrapper__description">
            {t('pages.holiday.overview.comment')}
          </p>
          <p className="holiday-overview__detail-wrapper__content">
            {holidayToDisplay.requestComment?.content}
          </p>
          <p className="holiday-overview__detail-wrapper__description">
            {t('pages.holiday.overview.message')}
          </p>
          <p className="holiday-overview__detail-wrapper__content">
            {holidayToDisplay.decisionComment?.content}
          </p>
        </div>
      )}
      <div className="holiday-overview__head">
        <Dropdown
          selectedOption={selectedYear.toString()}
          onChange={(year) => setSelectedYear(Number(year))}
          options={generateDropdownOptionsForYear()}
        />
        <p>
          {t('pages.holiday.overview.daysLeft')}: {vacationDaysLeft}
        </p>
      </div>
      <div className="holiday-overview__calendar-wrapper">
        <YearlyCalendar
          onPickDate={(date) =>
            setHolidayToDisplay(
              loadedHolidays.find((holiday) =>
                checkIfDayIsInRange(dayjs(date), holiday.startDate, holiday.endDate),
              ),
            )
          }
          year={selectedYear}
          customClasses={customClasses}
        />
      </div>
      <Box>
        <div className="holiday-overview__new-request__wrapper">
          <p className="holiday-overview__new-request__headline">
            {t('pages.holiday.overview.newRequest')}
          </p>
          {isReadOnly && (
            <p className="holiday-overview__new-request__warning">
              {t('pages.holiday.overview.noWorkSchedules')}
            </p>
          )}
          <div className="three-columns">
            <Input
              label={t('pages.holiday.overview.newRequestStart')}
              type="date"
              value={newHolidayRequest.startDate}
              onChangeDate={(startDate) => {
                if (startDate) handleUpdateHolidayRequest(startDate, true);
              }}
              disabled={isReadOnly}
            />
            <Input
              label={t('pages.holiday.overview.newRequestEnd')}
              disabled={isReadOnly}
              type="date"
              value={newHolidayRequest.endDate}
              onChangeDate={(endDate) => {
                if (endDate) handleUpdateHolidayRequest(endDate, false);
              }}
            />
            <Dropdown
              selectedOption={newHolidayRequest.reasonId}
              disabled={isReadOnly}
              options={reasonOptions}
              onChange={(reasonId) => {
                const isAzkReduction: boolean | undefined = loadedReasons.find(
                  (reason) => reason.id === reasonId,
                )?.azkReduction;
                setNewHolidayRequest({
                  ...newHolidayRequest,
                  reasonId,
                  onlyOn: isAzkReduction ? undefined : newHolidayRequest.onlyOn,
                });
              }}
              label={t('pages.holiday.overview.reason')}
            />
            <Input
              label={t('pages.holiday.overview.newRequestMessage')}
              type="text"
              value={newHolidayRequest.requestComment?.content || ''}
              onChange={(content) =>
                setNewHolidayRequest({
                  ...newHolidayRequest,
                  requestComment: { ...createComment(user.id, content) },
                })
              }
              disabled={isReadOnly}
            />
            <div>
              <Checkbox
                label={t('pages.holiday.overview.partialMorning')}
                onCheck={(checked) =>
                  setNewHolidayRequest({
                    ...newHolidayRequest,
                    totalAmount: checked ? 0.5 : 1,
                    onlyOn: checked ? DayPart.MORNING : undefined,
                  })
                }
                isChecked={newHolidayRequest.onlyOn === DayPart.MORNING}
                disabled={
                  isAzkReduction ||
                  !dayjs(newHolidayRequest.startDate).isSame(newHolidayRequest.endDate, 'date')
                }
              />
              <Checkbox
                label={t('pages.holiday.overview.partialAfternoon')}
                onCheck={(checked) =>
                  setNewHolidayRequest({
                    ...newHolidayRequest,
                    totalAmount: checked ? 0.5 : 1,
                    onlyOn: checked ? DayPart.AFTERNOON : undefined,
                  })
                }
                isChecked={newHolidayRequest.onlyOn === DayPart.AFTERNOON}
                disabled={
                  isAzkReduction ||
                  !dayjs(newHolidayRequest.startDate).isSame(newHolidayRequest.endDate, 'date')
                }
              />
            </div>
          </div>
          {saldoInformation}
          <Button
            maxWidth={300}
            type="button"
            value={t('general.buttons.request')}
            onClick={handleHolidayRequest}
            disabled={disableSubmit}
          />
        </div>
      </Box>
      <Box title={t('pages.holiday.overview.myRequests')}>
        <Table
          rows={requestRows}
          header={
            t('pages.holiday.overview.requestHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </div>
  ) : (
    <NotAllowed neccessaryRight={Right.HOLIDAY_OVERVIEW} />
  );
};
export default HolidayOverview;
